// UltraFlex - Day Pass Purchase Page (Public)
import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { MapPin, Calendar, CreditCard, AlertCircle, Check, ChevronRight, Dumbbell, Home } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DayPassPage = () => {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1); // 1: site, 2: date, 3: details, 4: confirm
  const [sites, setSites] = useState([]);
  const [price, setPrice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    site_id: '', first_name: '', last_name: '', email: '',
    confirmed_over_16: false, visit_date: '',
  });
  const cancelled = searchParams.get('cancelled');

  useEffect(() => {
    fetch(`${API_URL}/api/day-pass/sites`).then(r => r.json()).then(d => setSites(d)).catch(() => {});
    fetch(`${API_URL}/api/day-pass/price`).then(r => r.json()).then(d => setPrice(d)).catch(() => {});
  }, []);

  const selectedSite = sites.find(s => s.id === form.site_id);

  const validateEmail = (email) => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);

  const handleCheckout = async () => {
    setError('');
    if (!form.first_name.trim() || !form.last_name.trim()) { setError('Please enter your full name'); return; }
    if (!validateEmail(form.email)) { setError('Please enter a valid email address'); return; }
    if (!form.confirmed_over_16) { setError('You must confirm you are over 16'); return; }
    if (!form.site_id) { setError('Please select a gym'); return; }
    if (!form.visit_date) { setError('Please select a date'); return; }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/day-pass/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to create checkout');
      window.location.href = data.checkout_url;
    } catch (e) { setError(e.message); setLoading(false); }
  };

  // Generate calendar days for current and next month
  const today = new Date();
  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 30);

  const generateDays = () => {
    const days = [];
    const d = new Date(today);
    for (let i = 0; i < 30; i++) {
      days.push(new Date(d));
      d.setDate(d.getDate() + 1);
    }
    return days;
  };

  const steps = [
    { num: 1, label: 'Select Gym' },
    { num: 2, label: 'Choose Date' },
    { num: 3, label: 'Your Details' },
    { num: 4, label: 'Confirm & Pay' },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a1a]" data-testid="day-pass-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#e63946] to-[#c1121f] py-10 px-4">
        <div className="max-w-3xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-1.5 text-white/70 hover:text-white text-sm mb-4 transition-colors" data-testid="daypass-home-link">
            <Home className="w-4 h-4" /> Back to Home
          </Link>
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-3">
              <Dumbbell className="w-7 h-7 text-white" />
              <h1 className="text-3xl font-black text-white tracking-tight">ULTRAFLEX DAY PASS</h1>
            </div>
            <p className="text-white/80 text-base">Full gym access for a single day. No commitment.</p>
            {price && <p className="mt-2 text-3xl font-black text-white">{price.formatted}</p>}
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      {localStorage.getItem('uf_last_daypass_url') && (
        <div className="max-w-3xl mx-auto px-4 pt-4">
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl px-4 py-3 flex items-center justify-between" data-testid="retrieve-pass-banner">
            <p className="text-blue-400 text-sm">Already purchased a day pass?</p>
            <a href={localStorage.getItem('uf_last_daypass_url')} className="text-blue-400 hover:text-blue-300 text-sm font-semibold underline">View My Pass</a>
          </div>
        </div>
      )}
      <div className="max-w-3xl mx-auto px-4 py-6" data-testid="progress-steps">
        <div className="flex items-center justify-between">
          {steps.map((s, i) => (
            <React.Fragment key={s.num}>
              <div className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                  step >= s.num ? 'bg-[#e63946] text-white' : 'bg-[#1a1a2e] text-gray-500 border border-gray-700'
                }`}>
                  {step > s.num ? <Check className="w-5 h-5" /> : s.num}
                </div>
                <span className={`text-xs mt-1.5 ${step >= s.num ? 'text-[#e63946]' : 'text-gray-500'}`}>{s.label}</span>
              </div>
              {i < steps.length - 1 && <div className={`flex-1 h-0.5 mx-2 mt-[-18px] ${step > s.num ? 'bg-[#e63946]' : 'bg-gray-700'}`} />}
            </React.Fragment>
          ))}
        </div>
      </div>

      {cancelled && (
        <div className="max-w-3xl mx-auto px-4 mb-4">
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 text-yellow-400 text-sm flex items-center gap-2">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />Payment was cancelled. You can try again.
          </div>
        </div>
      )}

      {error && (
        <div className="max-w-3xl mx-auto px-4 mb-4">
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400 text-sm flex items-center gap-2" data-testid="error-message">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />{error}
          </div>
        </div>
      )}

      <div className="max-w-3xl mx-auto px-4 pb-16">
        {/* Step 1: Select Gym */}
        {step === 1 && (
          <div data-testid="step-select-site">
            <h2 className="text-xl font-bold text-white mb-4">Choose your gym</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {sites.map(s => (
                <button key={s.id} data-testid={`site-option-${s.id}`}
                  onClick={() => { setForm({...form, site_id: s.id}); setStep(2); setError(''); }}
                  className={`p-4 rounded-xl border text-left transition-all hover:border-[#e63946]/60 ${
                    form.site_id === s.id ? 'bg-[#e63946]/10 border-[#e63946]' : 'bg-[#1a1a2e] border-gray-700'
                  }`}>
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-[#e63946] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-white font-semibold text-sm">{s.name}</p>
                      {s.address && <p className="text-gray-400 text-xs mt-0.5">{s.address}</p>}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Pick Date */}
        {step === 2 && (
          <div data-testid="step-select-date">
            <h2 className="text-xl font-bold text-white mb-4">When do you want to visit {selectedSite?.name}?</h2>
            <div className="grid grid-cols-5 sm:grid-cols-7 gap-2">
              {generateDays().map(d => {
                const ds = d.toISOString().split('T')[0];
                const isSelected = form.visit_date === ds;
                const isToday = ds === today.toISOString().split('T')[0];
                return (
                  <button key={ds} data-testid={`date-option-${ds}`}
                    onClick={() => { setForm({...form, visit_date: ds}); setStep(3); setError(''); }}
                    className={`p-3 rounded-lg text-center transition-all ${
                      isSelected ? 'bg-[#e63946] text-white ring-2 ring-[#e63946]' :
                      'bg-[#1a1a2e] border border-gray-700 text-gray-300 hover:border-[#e63946]/60'
                    }`}>
                    <div className="text-[10px] uppercase">{d.toLocaleDateString('en-GB', { weekday: 'short' })}</div>
                    <div className="text-lg font-bold">{d.getDate()}</div>
                    <div className="text-[10px]">{d.toLocaleDateString('en-GB', { month: 'short' })}</div>
                    {isToday && <div className="text-[8px] text-[#e63946] font-bold">TODAY</div>}
                  </button>
                );
              })}
            </div>
            <button onClick={() => setStep(1)} className="mt-4 text-gray-400 text-sm hover:text-white">&larr; Back</button>
          </div>
        )}

        {/* Step 3: Details */}
        {step === 3 && (
          <div data-testid="step-details">
            <h2 className="text-xl font-bold text-white mb-4">Your details</h2>
            <div className="bg-[#1a1a2e] rounded-xl border border-gray-700 p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-xs mb-1.5">First Name</label>
                  <input data-testid="input-first-name" value={form.first_name}
                    onChange={e => setForm({...form, first_name: e.target.value})}
                    className="w-full bg-[#0a0a1a] border border-gray-700 rounded-lg px-4 py-2.5 text-white text-sm focus:border-[#e63946] focus:outline-none"
                    placeholder="Jane" />
                </div>
                <div>
                  <label className="block text-gray-400 text-xs mb-1.5">Last Name</label>
                  <input data-testid="input-last-name" value={form.last_name}
                    onChange={e => setForm({...form, last_name: e.target.value})}
                    className="w-full bg-[#0a0a1a] border border-gray-700 rounded-lg px-4 py-2.5 text-white text-sm focus:border-[#e63946] focus:outline-none"
                    placeholder="Smith" />
                </div>
              </div>
              <div>
                <label className="block text-gray-400 text-xs mb-1.5">Email Address</label>
                <input data-testid="input-email" type="email" value={form.email}
                  onChange={e => setForm({...form, email: e.target.value})}
                  className="w-full bg-[#0a0a1a] border border-gray-700 rounded-lg px-4 py-2.5 text-white text-sm focus:border-[#e63946] focus:outline-none"
                  placeholder="jane@example.com" />
                {form.email && !validateEmail(form.email) && (
                  <p className="text-red-400 text-xs mt-1">Please enter a valid email address</p>
                )}
              </div>
              <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg hover:bg-[#0a0a1a]" data-testid="checkbox-over-16">
                <input type="checkbox" checked={form.confirmed_over_16}
                  onChange={e => setForm({...form, confirmed_over_16: e.target.checked})}
                  className="w-5 h-5 accent-[#e63946] rounded" />
                <span className="text-white text-sm">I confirm I am over 16 years of age</span>
              </label>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setStep(2)} className="px-6 py-2.5 border border-gray-700 text-gray-300 rounded-lg hover:bg-[#1a1a2e] text-sm">&larr; Back</button>
              <button data-testid="continue-to-confirm" onClick={() => {
                if (!form.first_name.trim() || !form.last_name.trim()) { setError('Please enter your full name'); return; }
                if (!validateEmail(form.email)) { setError('Please enter a valid email address'); return; }
                if (!form.confirmed_over_16) { setError('You must confirm you are over 16'); return; }
                setError(''); setStep(4);
              }} className="flex-1 bg-[#e63946] text-white py-2.5 rounded-lg font-semibold hover:bg-[#c1121f] text-sm flex items-center justify-center gap-2">
                Continue <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Confirm & Pay */}
        {step === 4 && (
          <div data-testid="step-confirm">
            <h2 className="text-xl font-bold text-white mb-4">Confirm & Pay</h2>
            <div className="bg-[#1a1a2e] rounded-xl border border-gray-700 p-6 space-y-4">
              <div className="flex justify-between items-center py-3 border-b border-gray-700">
                <span className="text-gray-400 text-sm">Gym</span>
                <span className="text-white font-semibold text-sm">{selectedSite?.name}</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-gray-700">
                <span className="text-gray-400 text-sm">Visit Date</span>
                <span className="text-white font-semibold text-sm">
                  {form.visit_date && new Date(form.visit_date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-gray-700">
                <span className="text-gray-400 text-sm">Name</span>
                <span className="text-white font-semibold text-sm">{form.first_name} {form.last_name}</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-gray-700">
                <span className="text-gray-400 text-sm">Email</span>
                <span className="text-white font-semibold text-sm">{form.email}</span>
              </div>
              <div className="flex justify-between items-center py-3">
                <span className="text-gray-400 text-sm">Total</span>
                <span className="text-2xl font-black text-white">{price?.formatted || '£10.00'}</span>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setStep(3)} className="px-6 py-2.5 border border-gray-700 text-gray-300 rounded-lg hover:bg-[#1a1a2e] text-sm">&larr; Back</button>
              <button data-testid="pay-now-btn" onClick={handleCheckout} disabled={loading}
                className="flex-1 bg-[#e63946] text-white py-3 rounded-lg font-bold hover:bg-[#c1121f] text-sm flex items-center justify-center gap-2 disabled:opacity-50">
                <CreditCard className="w-5 h-5" />
                {loading ? 'Redirecting to payment...' : `Pay ${price?.formatted || '£10.00'}`}
              </button>
            </div>
            <p className="text-gray-500 text-xs text-center mt-4">Secure payment powered by Stripe. Your card details are never stored on our servers.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DayPassPage;
